import { useState, useEffect, useRef } from "react";
import { addMinutes, isPast } from "date-fns";

// This function checks if the stored object is structured as expected
// If it is now, the value is considered legacy. This function can be removed when the legacy block is removed
const isStructured = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: any,
): obj is { value: unknown; timestamp?: number } =>
  typeof obj === "object" &&
  obj !== null &&
  "value" in obj &&
  Object.keys(obj).every((k) => k === "value" || k === "timestamp");

type ExpiryOption = { expiresAt?: Date; expiresInMinutes?: number };

export const useLocalState = <T>(
  key: string,
  initialState: T,
  /**
   * Expiry options for the state
   * - `expiresAt`: A specific date when the state should expire.
   * - `expiresInMinutes`: The number of minutes after which the state should expire.
   * The expiry time is stored on first write and is NOT updated along with subsequent state updates.
   * After the expiry, the state will be reset to `initialState`.
   */
  options?: ExpiryOption,
) => {
  const expiredRef = useRef(false);
  const prevSerializedState = useRef<string | null>(null);
  const prevExpiresAt = useRef<number | undefined>(undefined);

  const initialExpiresAt = useRef<Date | undefined>(
    options?.expiresAt ??
      (options?.expiresInMinutes
        ? addMinutes(new Date(), options.expiresInMinutes)
        : undefined),
  );

  const loadState = (): T => {
    const stored = localStorage.getItem(key);
    if (!stored) return initialState;

    try {
      const parsed = JSON.parse(stored);

      // Handle legacy unstructured values
      if (!isStructured(parsed)) {
        const wrapped = {
          value: parsed,
          ...(initialExpiresAt.current
            ? { timestamp: initialExpiresAt.current.getTime() }
            : {}),
        };
        localStorage.setItem(key, JSON.stringify(wrapped));
        return (initialState instanceof Date ? new Date(parsed) : parsed) as T;
      }

      const { value, timestamp } = parsed;

      if (timestamp && isPast(new Date(timestamp))) {
        expiredRef.current = true;
        localStorage.removeItem(key);
        return initialState;
      }

      // If the value !== initialState, and the stored value is empty, we assume initialState has changed and overwrite it (no value is lost either way)
      if (
        value !== initialState && // initialState may have changed
        typeof value !== "boolean" && // skip booleans, otherwise it will always be caught by Object.keys(Boolean).length === 0
        (value === undefined ||
          value === null ||
          value === "" ||
          Object.keys(value).length === 0 || // empty object
          (Array.isArray(value) && value.length === 0)) // empty array
      ) {
        localStorage.removeItem(key);
        return initialState;
      }

      return (
        initialState instanceof Date ? new Date(value as string) : value
      ) as T;
    } catch {
      return initialState;
    }
  };

  const [state, setState] = useState<T>(() => loadState());

  useEffect(() => {
    if (expiredRef.current) {
      expiredRef.current = false;
      return;
    }

    const currentSerialized =
      typeof state === "string" ||
      typeof state === "number" ||
      typeof state === "boolean"
        ? String(state)
        : JSON.stringify(state);

    const currentExpiresAt = initialExpiresAt.current?.getTime();

    const shouldUpdate =
      currentSerialized !== prevSerializedState.current ||
      currentExpiresAt !== prevExpiresAt.current;

    if (shouldUpdate) {
      localStorage.setItem(
        key,
        JSON.stringify({
          value: state instanceof Date ? state.toISOString() : state,
          ...(currentExpiresAt ? { timestamp: currentExpiresAt } : {}),
        }),
      );
      prevSerializedState.current = currentSerialized;
      prevExpiresAt.current = currentExpiresAt;
      window.dispatchEvent(
        new CustomEvent("local-storage", { detail: { key } }),
      );
    }
  }, [key, state]);

  // Listen for changes from other components/tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent | CustomEvent) => {
      const eventKey = e instanceof StorageEvent ? e.key : e.detail?.key;

      if (eventKey === key) {
        const newState = loadState();
        setState(newState);

        // Update the ref to prevent the main useEffect from writing back to localStorage (echo prevention)
        const serialized =
          typeof newState === "string" ||
          typeof newState === "number" ||
          typeof newState === "boolean"
            ? String(newState)
            : JSON.stringify(newState);
        prevSerializedState.current = serialized;
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(
      "local-storage",
      handleStorageChange as EventListener,
    );

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "local-storage",
        handleStorageChange as EventListener,
      );
    };
  }, [key]);

  return [state, setState] as const;
};
